import { useState, type ReactNode } from "react";
import { Icon } from "../components/Icon";
import { COURSE_PROGRAM, COURSE_TITLE } from "../course/outline";

/**
 * Layout híbrido de tres columnas: navegación del curso a la izquierda, el
 * stream (o el preset a pantalla completa) en el centro, y las fuentes citadas
 * a la derecha.
 *
 * Las columnas laterales no son decorado: se pueden plegar. En pantallas
 * estrechas se convierten en paneles superpuestos y el centro —que es donde
 * ocurre la clase— se queda con todo el ancho.
 */

export interface AppShellProps {
  nav: ReactNode;
  main: ReactNode;
  rail: ReactNode;
  status: ReactNode;
  /** Cuando hay un preset activo, el centro cambia de registro. */
  stageMode?: boolean;
}

export function AppShell({ nav, main, rail, status, stageMode = false }: AppShellProps) {
  const [navOpen, setNavOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false);

  return (
    <div className="shell" data-nav-open={navOpen} data-rail-open={railOpen} data-stage={stageMode}>
      <header className="shell__header">
        <div className="shell__brand">
          <span className="shell__mark" aria-hidden="true">
            <Icon name="sigma" size="1.125rem" />
          </span>
          <span className="shell__titles">
            <span className="shell__title">{COURSE_TITLE}</span>
            <span className="shell__program">{COURSE_PROGRAM}</span>
          </span>
        </div>

        <div className="shell__status">{status}</div>

        <div className="shell__toggles">
          <button
            type="button"
            className="u-button u-button--ghost u-button--small shell__toggle"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((value) => !value)}
          >
            <Icon name="list" size="0.875rem" />
            Curso
          </button>
          <button
            type="button"
            className="u-button u-button--ghost u-button--small shell__toggle shell__sources-toggle"
            aria-expanded={railOpen}
            onClick={() => setRailOpen((value) => !value)}
          >
            <Icon name="book" size="0.875rem" />
            Fuentes
          </button>
        </div>
      </header>

      <div className="shell__grid">
        <aside className="shell__nav" aria-label="Navegación del curso" onClick={(event) => {
          if (event.target instanceof Element && event.target.closest("button")) setNavOpen(false);
        }}>
          {nav}
        </aside>
        <main className="shell__main" aria-label="Conversación">
          {main}
        </main>
        <aside className="shell__rail" aria-label="Fuentes citadas">
          {rail}
        </aside>
      </div>

      {(navOpen || railOpen) && (
        <button
          type="button"
          className="shell__scrim"
          aria-label="Cerrar paneles"
          onClick={() => {
            setNavOpen(false);
            setRailOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default AppShell;
